/*
 * nt-precut-cart.js — keeps the hidden "Pre-cut Option" fee line a slave of the
 * precut transfers in the cart. Companion to the add-time fee push in
 * bySize_popular.js (which adds the fee at the qty chosen on the PDP). This script
 * enforces the three cart rules:
 *   1. The fee line's quantity is NOT user-editable — its qty stepper and Remove
 *      control are hidden on both the cart page and the cart drawer/slider.
 *   2. When a precut transfer's quantity changes (or one is added/removed), the fee
 *      qty re-syncs.
 *   3. Fee qty = the summed quantity of every cart line added with `Precut: Yes`.
 *      When that total is 0 (no precut transfers left) the fee line removes itself.
 *
 * The fee line is matched by variant id in /cart.js (reliable), and located in the
 * DOM by its line key (rows carry data-id="{{ item.key }}", no variant-id). Repaint
 * after a sync uses the theme's own refreshCart('notOpen') (re-renders without
 * opening the drawer). Guarded against re-entrancy so our own /cart/change.js for
 * the fee doesn't trigger another reconcile.
 */
(function () {
  'use strict';

  /* The "Pre-cut Option" fee variant. window.NT_PRECUT_FEE_VID is rendered on the
     PDP (dtf-transfers) by theme.liquid; on the cart page it isn't set, so fall back
     to the known id. Keep this fallback in sync with theme.liquid. */
  var FEE_VID = ( typeof window.NT_PRECUT_FEE_VID !== 'undefined' && window.NT_PRECUT_FEE_VID )
    ? window.NT_PRECUT_FEE_VID
    : 46824811626685;

  var syncing = false;     // true while WE issue the fee /cart/change.js (avoid loops)
  var debTimer = null;
  var feeKey = null;       // last known fee line key, for locking after re-renders

  function eachOf( list, fn ) { Array.prototype.forEach.call( list, fn ); }

  /* Rule 1 — hide the fee line's qty stepper + remove so it can't be edited. */
  function lockFeeRows() {
    if ( !feeKey ) return;
    var sel = '[data-js-cart-item][data-id="' + feeKey.replace( /"/g, '\\"' ) + '"]';
    eachOf( document.querySelectorAll( sel ), function ( row ) {
      eachOf( row.querySelectorAll( '.qty-button' ), function ( b ) { b.style.display = 'none'; } );
      eachOf( row.querySelectorAll( '.qty, .qty-selector, .product__quantity' ), function ( inp ) {
        inp.readOnly = true;
        inp.setAttribute( 'disabled', 'disabled' );
        inp.style.pointerEvents = 'none';
        inp.style.background = 'transparent';
        inp.style.textAlign = 'center';
      } );
      /* The .remove control is intentionally LEFT VISIBLE so a customer can change
         their mind and back out of precut. Removing the fee reverts the transfers to
         non-precut (see reconcile() -> revertPrecut). Only the QUANTITY is locked. */
    } );
  }

  /* Rules 2 & 3 (+ Remove handling): keep the fee qty = sum of Precut:Yes line
     quantities; remove the fee when that total is 0; and if the fee was removed by
     the user while precut transfers remain, revert those transfers to non-precut. */
  function reconcile() {
    if ( syncing ) return;
    fetch( '/cart.js', { headers: { 'Accept': 'application/json' } } )
      .then( function ( r ) { return r.json(); } )
      .then( function ( cart ) {
        var fee = null, total = 0, precutLines = [];
        ( cart.items || [] ).forEach( function ( it ) {
          if ( it.variant_id === FEE_VID ) {
            fee = it;
          } else if ( it.properties && it.properties[ 'Precut' ] === 'Yes' ) {
            total += ( it.quantity || 0 );
            precutLines.push( it );
          }
        } );

        if ( fee ) {
          feeKey = fee.key; lockFeeRows();
          if ( fee.quantity === total ) return;   // already correct
          syncFee( fee.key, total );               // keep fee qty = total (0 removes the line)
        } else if ( total > 0 ) {
          // Fee line is gone but transfers are still flagged Precut: Yes — the user
          // removed the fee (changed their mind). Revert those transfers to non-precut
          // so they aren't fulfilled as precut without a charge.
          revertPrecut( precutLines );
        }
      } )
      .catch( function () {} );
  }

  function syncFee( key, qty ) {
    syncing = true;
    fetch( '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify( { id: key, quantity: qty } ) // quantity 0 removes the line
    } ).then( function ( r ) { return r.json(); } ).then( afterWrite ).catch( function () { syncing = false; } );
  }

  function revertPrecut( lines ) {
    if ( !lines.length ) return;
    syncing = true;
    var chain = Promise.resolve();
    lines.forEach( function ( it ) {
      var props = {};
      Object.keys( it.properties || {} ).forEach( function ( k ) { if ( k !== 'Precut' ) props[ k ] = it.properties[ k ]; } );
      chain = chain.then( function () {
        return fetch( '/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify( { id: it.key, quantity: it.quantity, properties: props } )
        } );
      } );
    } );
    chain.then( afterWrite ).catch( function () { syncing = false; } );
  }

  function afterWrite() {
    syncing = false;
    if ( typeof window.refreshCart === 'function' ) { try { window.refreshCart( 'notOpen' ); } catch ( e ) {} }
    setTimeout( lockFeeRows, 400 );
  }

  function schedule() {
    if ( debTimer ) clearTimeout( debTimer );
    debTimer = setTimeout( reconcile, 200 );
  }

  /* Trigger 1: the theme dispatches 'cart-updated' on <cart-form> after every
     qty/remove re-render (cart page + drawer share component-cart.js). */
  document.addEventListener( 'cart-updated', function () { lockFeeRows(); schedule(); }, true );

  /* Trigger 2: catch any cart mutation directly (covers paths that don't dispatch
     cart-updated — e.g. the PDP add — and is a safety net). Our own fee change is
     skipped via the `syncing` guard so it can't loop. */
  if ( window.fetch ) {
    var origFetch = window.fetch;
    window.fetch = function ( input ) {
      var url = ( typeof input === 'string' ) ? input : ( input && input.url ) || '';
      var isMutation = /\/cart\/(change|update|add)(\.js)?/.test( url );
      var p = origFetch.apply( this, arguments );
      if ( isMutation && !syncing ) { p.then( function () { schedule(); } ).catch( function () {} ); }
      return p;
    };
  }

  /* Re-apply the lock whenever the cart container re-renders (innerHTML is replaced
     wholesale by the Section Rendering update / refreshCart). */
  if ( window.MutationObserver ) {
    var form = document.getElementById( 'AjaxCartForm' );
    if ( form ) new MutationObserver( lockFeeRows ).observe( form, { childList: true, subtree: true } );
  }

  /* Initial pass: only when a cart actually has rendered line items (cart page or a
     populated drawer), so we don't fetch /cart.js on every empty page. */
  function init() { if ( document.querySelector( '[data-js-cart-item]' ) ) schedule(); }
  if ( document.readyState !== 'loading' ) init();
  document.addEventListener( 'DOMContentLoaded', init );
})();
